import type { TFunction } from 'react-i18next';

import type { MapEditorCatalogElement } from './mapEditorCatalog';
import { spotCountOf } from './mapEditorRoots';

// What is on a module's walls, in words. One phrase, asked for in three places — the picker's root
// cards, the rows of the walk, and the segment panel's subtitle.
//
// One function rather than three, because it was three and one of them was wrong: the segment panel
// reached for the plural key unconditionally and rendered "1 pictures" for every single-frame module,
// while the other two branched on the count. A phrase repeated in three components is a phrase that
// only has to be got right twice to look right.
//
// Written here rather than beside `spotCountOf` for the reason mapEditorRefusalText is a module of
// its own: counting what a module holds is a question about the catalog, and saying it in the
// author's language is not.

/**
 * What is on this module's walls, as the map editor says it.
 *
 * @param element - The module, or undefined for an id the catalog cannot describe — which says
 * nothing rather than guessing.
 * @param translate - i18next `t`, passed in so this stays a plain function.
 * @returns A short phrase, or empty when the build measured nothing. Empty is the correct answer for
 * an unmeasured module and is not the same as "bare walls": one shipped module genuinely holds
 * nothing, and telling an author it is unmeasured would be a different and wrong statement.
 */
export const describeSpotCount = (
  element: MapEditorCatalogElement | undefined,
  translate: TFunction,
): string => {
  if (!element) {
    return '';
  }

  const { kind, total } = spotCountOf(element);

  if (kind === 'unknown') {
    return '';
  }

  if (kind === 'bare') {
    return translate('editorMode.mapEditorSpotsBare', 'bare walls');
  }

  if (kind === 'screens') {
    return total === 1
      ? translate('editorMode.mapEditorSpotsScreenSingular', '1 screen')
      : translate(
          'editorMode.mapEditorSpotsScreenPlural',
          '{{total}} screens',
          {
            total,
          },
        );
  }

  return total === 1
    ? translate('editorMode.mapEditorSpotsPictureSingular', '1 picture')
    : translate(
        'editorMode.mapEditorSpotsPicturePlural',
        '{{total}} pictures',
        {
          total,
        },
      );
};
